import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

/**
 * Pennsylvania Professional License Renewal Landing Page
 */

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const PALicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 24,
      cycleYears: 2,
      terminology: 'LU',
      highlights: ['24 LU biennial', 'Min 8 LU in Health, Safety & Welfare', '1 LU in Accessibility (mandatory)', 'Max 12 LU self-study', 'Reportable by September 30 renewal date'],
      stateBoard: 'Pennsylvania State Architects Licensure Board',
      boardUrl: 'https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/Architecture/'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 24,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['24 PDH biennial', '2 PDH in Ethics or Professionalism (mandatory)', 'Technical engineering subjects', 'Min 12 PDH per year', 'Max 12 PDH self-study per biennium'],
      stateBoard: 'Pennsylvania State Registration Board for Professional Engineers',
      boardUrl: 'https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/Engineers/'
    },
    {
      id: 'PLS',
      name: 'Professional Land Surveyor',
      icon: '📐',
      credits: 24,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['24 PDH biennial', '2 PDH in Ethics or Professionalism (mandatory)', 'Land surveying topics', 'Min 12 PDH per year', 'Professional development activities'],
      stateBoard: 'Pennsylvania State Registration Board for Professional Engineers, Land Surveyors & Geologists',
      boardUrl: 'https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/Engineers/'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 80,
      cycleYears: 2,
      terminology: 'CPE',
      highlights: ['80 CPE hours biennial', 'Min 20 hours annually', '4 hours Ethics (every 2 years)', 'Min 40% technical accounting subjects', 'Max 20 hours self-study per year'],
      stateBoard: 'Pennsylvania State Board of Accountancy',
      boardUrl: 'https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/Accountancy/'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 30,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['30 contact hours biennial', 'Nursing practice-related', 'Max 15 hours self-study', 'Approved CE providers', 'Active practice or refresher course recommended'],
      stateBoard: 'Pennsylvania State Board of Nursing',
      boardUrl: 'https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/Nursing/'
    },
    {
      id: 'CRNP',
      name: 'Certified Registered Nurse Practitioner',
      icon: '🩺',
      credits: 30,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['30 contact hours biennial', 'Advanced practice nursing topics', 'Must maintain national certification', 'Max 15 hours self-study', 'Clinical competency focus'],
      stateBoard: 'Pennsylvania State Board of Nursing',
      boardUrl: 'https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/Nursing/'
    },
    {
      id: 'LPC',
      name: 'Licensed Professional Counselor',
      icon: '💭',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '3 hours Ethics (mandatory)', 'Professional counseling topics', 'Max 15 hours online/self-study', 'Approved CE providers'],
      stateBoard: 'Pennsylvania State Board of Social Workers, Marriage & Family Therapists and Professional Counselors',
      boardUrl: 'https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/SocialWorkers/'
    },
    {
      id: 'LCSW',
      name: 'Licensed Clinical Social Worker',
      icon: '🧠',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '3 hours Ethics (mandatory)', 'Clinical social work practice', 'Max 15 hours online/self-study', 'Evidence-based interventions'],
      stateBoard: 'Pennsylvania State Board of Social Workers, Marriage & Family Therapists and Professional Counselors',
      boardUrl: 'https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/SocialWorkers/'
    },
    {
      id: 'LMFT',
      name: 'Licensed Marriage & Family Therapist',
      icon: '👨‍👩‍👧‍👦',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '3 hours Ethics (mandatory)', 'Marriage and family therapy topics', 'Max 15 hours online/self-study', 'Systems-based approaches'],
      stateBoard: 'Pennsylvania State Board of Social Workers, Marriage & Family Therapists and Professional Counselors',
      boardUrl: 'https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/SocialWorkers/'
    },
    {
      id: 'PSY',
      name: 'Licensed Psychologist',
      icon: '🧑‍⚕️',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '3 hours Ethics (mandatory)', 'Psychological practice topics', 'Max 15 hours online/self-study', 'Clinical or research-oriented'],
      stateBoard: 'Pennsylvania State Board of Psychology',
      boardUrl: 'https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/Psychology/'
    },
    {
      id: 'RE',
      name: 'Real Estate Broker/Salesperson',
      icon: '🏠',
      credits: 14,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['14 hours biennial', 'State-approved courses', 'Real estate practice topics', 'Completion required before renewal', 'Online courses accepted'],
      stateBoard: 'Pennsylvania State Real Estate Commission',
      boardUrl: 'https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/RealEstateCommission/'
    },
    {
      id: 'ATT',
      name: 'Attorney',
      icon: '⚖️',
      credits: 12,
      cycleYears: 1,
      terminology: 'CLE',
      highlights: ['12 CLE hours annually (active status)', '2 hours Ethics (mandatory)', '1 hour Professionalism (mandatory)', 'Accredited programs required', 'Max 6 hours online per year'],
      stateBoard: 'Pennsylvania Continuing Legal Education Board',
      boardUrl: 'https://www.pacle.org/'
    }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Pennsylvania Professional License Renewal Requirements",
      "description": "Complete guide to Pennsylvania professional license renewal requirements for Architects, Engineers, CPAs, Nurses, Social Workers, and more.",
      "url": "https://cectrack.com/pa-license-renewal",
      "about": {
        "@type": "Thing",
        "name": "Professional License Renewal",
        "sameAs": [
          "https://www.dos.pa.gov/",
          "https://www.pacle.org/"
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "CEC Track",
        "url": "https://cectrack.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cectrack.com/og-image.png"
        }
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": "Pennsylvania Professional Licenses",
        "numberOfItems": 12,
        "itemListElement": professions.map((prof, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Course",
            "name": prof.name,
            "description": `${prof.name} license renewal requires ${prof.credits} ${prof.terminology} every ${prof.cycleYears} year(s)`,
            "provider": {
              "@type": "Organization",
              "name": prof.stateBoard,
              "url": prof.boardUrl
            }
          }
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://cectrack.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Pennsylvania License Renewal",
            "item": "https://cectrack.com/pa-license-renewal"
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do I need to renew my Pennsylvania professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CE requirements vary by profession in Pennsylvania. Architects, Engineers, Surveyors, RNs, CRNPs, and Mental Health professionals need 24-30 hours biennial; CPAs need 80 CPE hours biennial; Real Estate professionals need 14 hours biennial; Attorneys need 12 CLE hours annually."
          }
        },
        {
          "@type": "Question",
          "name": "Which state agency handles professional license renewal in Pennsylvania?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The Pennsylvania Department of State - Bureau of Professional and Occupational Affairs oversees most professional licensing boards including Architects, Engineers, CPAs, Nurses, Social Workers, Counselors, Therapists, Psychologists, and Real Estate professionals. The Pennsylvania CLE Board administers attorney CLE requirements separately."
          }
        },
        {
          "@type": "Question",
          "name": "What are the mandatory CE topics for Pennsylvania professionals?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Pennsylvania has several mandatory CE requirements: Architects need 8 LU in HSW and 1 LU in Accessibility; Engineers and Surveyors need 2 PDH in Ethics/Professionalism; CPAs need 4 hours Ethics every 2 years; Mental Health professionals and Psychologists need 3 hours Ethics; Attorneys need 2 hours Ethics and 1 hour Professionalism annually."
          }
        },
        {
          "@type": "Question",
          "name": "How often do I need to renew my Pennsylvania professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most Pennsylvania professions renew biennially (every 2 years). Attorneys renew annually with 12 CLE hours per year. Renewal dates vary by profession and often align with birth months or license expiration dates."
          }
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "Pennsylvania Department of State - Bureau of Professional and Occupational Affairs",
      "alternateName": "PA BPOA",
      "url": "https://www.dos.pa.gov/ProfessionalLicensing/",
      "telephone": "+1-717-783-7200",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "PO Box 2649",
        "addressLocality": "Harrisburg",
        "addressRegion": "PA",
        "postalCode": "17105",
        "addressCountry": "US"
      },
      "areaServed": "Pennsylvania",
      "description": "Oversees professional licensing for Architects, Engineers, CPAs, Nurses, Social Workers, Counselors, Therapists, Psychologists, and Real Estate professionals in Pennsylvania"
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "Pennsylvania Continuing Legal Education Board",
      "url": "https://www.pacle.org/",
      "telephone": "+1-717-231-3350",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "5035 Ritter Road, Suite 700",
        "addressLocality": "Mechanicsburg",
        "addressRegion": "PA",
        "postalCode": "17055",
        "addressCountry": "US"
      },
      "areaServed": "Pennsylvania",
      "description": "Administers Continuing Legal Education (CLE) requirements for attorneys licensed in Pennsylvania"
    }
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="Pennsylvania Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to Pennsylvania professional license renewal requirements. Track CE credits for Architects, Engineers, CPAs, Nurses, Social Workers, Attorneys, and more."
        keywords="Pennsylvania license renewal, PA CE requirements, PA continuing education, Pennsylvania professional licenses, PA BPOA, Pennsylvania nurses, PA CPAs, PA engineers, PA attorneys"
        canonical="https://cectrack.com/pa-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section 
        aria-label="Page header"
        style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                Pennsylvania Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Never miss a deadline. Track your continuing education requirements across all your Pennsylvania professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link 
                  to="/register" 
                  className="btn btn-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                >
                  Start Tracking Free
                </Link>
                <a 
                  href="#professions" 
                  className="btn btn-outline-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }} aria-label="12 professions">12</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }} aria-label="Over 50 categories">50+</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }} aria-label="100 percent compliant">100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>BPOA Compliant</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }} aria-label="24/7 access">24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professions Grid */}
      <main id="main-content">
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
                Pennsylvania CE Requirements by Profession
              </h2>
              <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
                Select your profession to see detailed continuing education requirements
              </p>
            </div>

          <div className="row g-4">
            {professions.map((prof) => (
              <div key={prof.id} className="col-md-6 col-lg-4">
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ 
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                    <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>
                      {prof.name}
                    </h5>
                    
                    <div 
                      style={{
                        backgroundColor: '#dbeafe',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e40af' }}>
                        {prof.credits} {prof.terminology}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                        {prof.cycleYears === 1 ? 'Annual' : 'Biennial'}
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                        Key Requirements:
                      </div>
                      <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                        {prof.highlights.map((highlight, idx) => (
                          <li key={idx} style={{ marginBottom: '0.3rem' }}>{highlight}</li>
                        ))}
                      </ul>
                    </div>

                    <div 
                      style={{
                        paddingTop: '1rem',
                        borderTop: '1px solid #e9ecef',
                        fontSize: '0.85rem',
                        color: '#6c757d'
                      }}
                    >
                      <strong>{prof.stateBoard}</strong>
                      <br />
                      <a 
                        href={prof.boardUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#3b82f6', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        Visit State Board →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CEC Track */}
      <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                Simplify Your Pennsylvania License Management
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                Managing multiple Pennsylvania professional licenses doesn't have to be complicated. 
                CEC Track automatically tracks your requirements, calculates your progress, and sends 
                timely reminders so you never miss a renewal deadline.
              </p>
              <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                {[
                  'Automated deadline tracking for all PA professions',
                  'Upload certificates and match to mandatory topics',
                  'Real-time progress reports by category',
                  'Multi-state support for professionals licensed in multiple states',
                  'Secure cloud storage for all documents',
                  'Email notifications for upcoming deadlines'
                ].map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#3b82f6', fontSize: '1.5rem' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div 
                className="p-5 text-center"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>
                  Start Tracking Today
                </h3>
                <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                  Join hundreds of Pennsylvania professionals who trust CEC Track to manage their continuing education.
                </p>
                <Link 
                  to="/register" 
                  className="btn btn-primary btn-lg px-5 py-3"
                  style={{ 
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                >
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Resources */}
      <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Pennsylvania State Resources
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Official resources and contact information for Pennsylvania professional licensing
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    PA Bureau of Professional & Occupational Affairs
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Oversees professional licensing boards for Architects, Engineers, Surveyors, CPAs, Nurses, Social Workers, Counselors, Therapists, Psychologists, and Real Estate professionals.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+17177837200" aria-label="Call BPOA at 717-783-7200">(717) 783-7200</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.dos.pa.gov/ProfessionalLicensing/" target="_blank" rel="noopener noreferrer" aria-label="Visit BPOA website">
                      dos.pa.gov/ProfessionalLicensing
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> PO Box 2649, Harrisburg, PA 17105
                  </div>
                  <a 
                    href="https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View all professional licensing boards"
                  >
                    View All Licensing Boards
                  </a>
                </div>
              </article>
            </div>

            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Pennsylvania CLE Board
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Administers Continuing Legal Education (CLE) requirements for attorneys licensed in Pennsylvania. Mandatory annual CLE includes Ethics and Professionalism.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+17172313350" aria-label="Call CLE Board at 717-231-3350">(717) 231-3350</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.pacle.org/" target="_blank" rel="noopener noreferrer" aria-label="Visit CLE Board website">
                      www.pacle.org
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 5035 Ritter Road, Suite 700, Mechanicsburg, PA 17055
                  </div>
                  <a 
                    href="https://www.pacle.org/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View CLE requirements"
                  >
                    View CLE Requirements
                  </a>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Other States Cross-Linking */}
      <OtherStates currentState="PA" />

      {/* CTA Section */}
      <section 
        aria-label="Call to action"
        style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: 'white',
          padding: '80px 0'
        }}
      >
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
            Ready to Simplify Your Pennsylvania License Renewal?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
            Join professionals across Pennsylvania who never worry about missing CE requirements
          </p>
          <Link 
            to="/register" 
            className="btn btn-light btn-lg px-5 py-3"
            style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}
          >
            Get Started Free
          </Link>
        </div>
      </section>
      </main>
    </>
  );
};

export default PALicenseRenewal;
